import { useCallback, useEffect, useState } from 'react';
import { handleCheckRangeValue } from '../utils';
import { useTradingProtection } from './useTradingProtection';
import { IEditingTradingFields, IInstrument, IRealPrice } from '../types';
import { getMaxDigitWithDot } from '@trader/utils';
import { useI18next } from '@trader/services';

const initialErrors = {
  marketAmountError: '',
  limitAmountError: '',
  limitPriceError: '',
  stopLossError: '',
  takeProfitError: '',
} as const;

interface IHandleErrors {
  value: string;
  errorName: keyof typeof initialErrors;
  minValue: number;
  maxValue: number;
  increment: number;
  fixDigitCustom?: number;
  isAmount?: boolean;
}

export interface IUseTradingValidationsOutput {
  errors: typeof initialErrors;
  isValidationErrors: boolean;
  handleSomeError: (props: IHandleErrors) => void;
  removeProtectionErrors: () => void;
  removeSomeError: (name: keyof typeof initialErrors) => void;
}

export const useTradingValidations = (
  instrument: IInstrument,
  realTimePrice: IRealPrice,
  isEditedFields: IEditingTradingFields
): IUseTradingValidationsOutput => {
  const { maxProfitValue, maxLossValue, minLossValue, minProfitValue } =
    useTradingProtection(instrument, realTimePrice);
  const { general, market, limit } = instrument;
  const [errors, setErrors] = useState<typeof initialErrors>(initialErrors);

  useEffect(() => {
    return () => {
      setErrors(initialErrors);
    };
  }, []);

  useEffect(() => {
    if (isEditedFields.marketAmount) {
      handleSomeError({
        value: market.amountVolume,
        errorName: 'marketAmountError',
        minValue: market.minAmountValue,
        maxValue: market.maxAmountValue,
        increment: general.step,
        isAmount: true,
      });
    }
  }, [isEditedFields.marketAmount, market.amountVolume]);

  useEffect(() => {
    if (isEditedFields.limitAmount) {
      handleSomeError({
        value: limit.amount,
        errorName: 'limitAmountError',
        minValue: market.minAmountValue,
        maxValue: market.maxAmountValue,
        increment: general.step,
        isAmount: true,
      });
    }
  }, [isEditedFields.limitAmount, limit.amount, realTimePrice.ask]);

  useEffect(() => {
    if (isEditedFields.stopLoss) {
      handleSomeError({
        value: market.protection.lossValue,
        errorName: 'stopLossError',
        minValue: minLossValue,
        maxValue: maxLossValue,
        fixDigitCustom: getMaxDigitWithDot(general.pipSize),
        increment: general.step,
        isAmount: false,
      });
    }
    if (isEditedFields.takeProfit) {
      handleSomeError({
        value: market.protection.profitValue,
        errorName: 'takeProfitError',
        minValue: minProfitValue,
        maxValue: maxProfitValue,
        fixDigitCustom: getMaxDigitWithDot(general.pipSize),
        increment: general.step,
        isAmount: false,
      });
    }
  }, [
    isEditedFields.takeProfit,
    isEditedFields.stopLoss,
    market.protection.lossValue,
    market.protection.profitValue,
    market.billType,
    realTimePrice.ask,
  ]);

  const removeProtectionErrors = useCallback(
    () =>
      setErrors(prev => ({
        ...prev,
        takeProfitError: '',
        stopLossError: '',
      })),
    []
  );

  const removeSomeError = useCallback(
    errorName =>
      setErrors(prev => ({
        ...prev,
        [errorName]: '',
      })),
    []
  );

  const { translate } = useI18next();

  const handleSomeError: IUseTradingValidationsOutput['handleSomeError'] = ({
    value,
    errorName,
    minValue,
    maxValue,
    increment,
    fixDigitCustom,
    isAmount,
  }) => {
    setErrors(prev => ({
      ...prev,
      [errorName]: handleCheckRangeValue(
        value,
        minValue,
        maxValue,
        increment,
        fixDigitCustom || general.fixDigitAfterDot,
        translate,
        isAmount
      ),
    }));
  };

  return {
    errors,
    isValidationErrors: Object.values(errors).some(e => !!e),
    handleSomeError,
    removeProtectionErrors,
    removeSomeError,
  };
};
